import React, { useState, useEffect, createContext, useContext } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';

// Modal Alert Types
export const ALERT_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error', 
  INFO: 'info',
  WARNING: 'warning'
};

// Create Context for Modal Alert
const ModalAlertContext = createContext(null);

// Custom hook for using modal alerts (now uses context)
export const useModalAlert = () => {
  const context = useContext(ModalAlertContext);
  if (!context) {
    throw new Error('useModalAlert must be used within a ModalAlertProvider');
  }
  return context;
};

// Internal hook for managing modal alert state
const useModalAlertState = () => {
  const [alert, setAlert] = useState(null);

  const showAlert = (type, title, message, options = {}) => {
    setAlert({
      type,
      title,
      message,
      ...options,
      id: Date.now() // Unique ID for each alert
    });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const showSuccess = (title, message, options = {}) => 
    showAlert(ALERT_TYPES.SUCCESS, title, message, options);
    
  const showError = (title, message, options = {}) => 
    showAlert(ALERT_TYPES.ERROR, title, message, options);
    
  const showInfo = (title, message, options = {}) => 
    showAlert(ALERT_TYPES.INFO, title, message, options);
    
  const showWarning = (title, message, options = {}) => 
    showAlert(ALERT_TYPES.WARNING, title, message, options);

  return {
    alert,
    showAlert,
    hideAlert,
    showSuccess,
    showError,
    showInfo,
    showWarning
  };
};

// Main Modal Alert Component
const ModalAlert = ({
  isOpen,
  onClose,
  onConfirm,
  type = ALERT_TYPES.INFO,
  title,
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  showCancel = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getAlertConfig = () => {
    switch (type) {
      case ALERT_TYPES.SUCCESS:
        return {
          icon: FaCheckCircle,
          iconColor: 'text-green-400',
          bgColor: 'bg-green-900/20',
          borderColor: 'border-green-500/30',
          accentColor: 'bg-green-500'
        };
      case ALERT_TYPES.ERROR:
        return {
          icon: FaExclamationTriangle,
          iconColor: 'text-red-400',
          bgColor: 'bg-red-900/20',
          borderColor: 'border-red-500/30',
          accentColor: 'bg-red-500'
        };
      case ALERT_TYPES.WARNING:
        return {
          icon: FaExclamationTriangle,
          iconColor: 'text-yellow-400',
          bgColor: 'bg-yellow-900/20',
          borderColor: 'border-yellow-500/30',
          accentColor: 'bg-yellow-500'
        };
      default: // INFO
        return {
          icon: FaInfoCircle,
          iconColor: 'text-cyan-400',
          bgColor: 'bg-cyan-900/20',
          borderColor: 'border-cyan-500/30',
          accentColor: 'bg-cyan-500'
        };
    }
  };

  const config = getAlertConfig();
  const Icon = config.icon;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div 
        className={`relative w-full max-w-md mx-auto bg-slate-800 rounded-xl shadow-2xl border ${config.borderColor} transform transition-all duration-200 scale-100`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        {/* Header with accent bar */}
        <div className={`h-1 w-full ${config.accentColor} rounded-t-xl`} />
        
        {/* Content */}
        <div className="p-6">
          {/* Icon and Title */}
          <div className="flex items-start gap-4 mb-4">
            <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full ${config.bgColor}`}>
              <Icon className={`w-5 h-5 ${config.iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="modal-title" className="text-lg font-semibold text-white mb-2">
                {title}
              </h3>
              {message && (
                <p id="modal-description" className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                  {message}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700/50"
              aria-label="Close modal"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end">
            {showCancel && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500"
              >
                {cancelText}
              </button>
            )}
            <button
              onClick={handleConfirm}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                type === ALERT_TYPES.ERROR 
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : type === ALERT_TYPES.SUCCESS
                  ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  : type === ALERT_TYPES.WARNING
                  ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                  : 'bg-cyan-600 hover:bg-cyan-700 focus:ring-cyan-500'
              }`}
              autoFocus
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Provider component for global modal alert management
export const ModalAlertProvider = ({ children }) => {
  const alertState = useModalAlertState();

  return (
    <ModalAlertContext.Provider value={alertState}>
      {children}
      <ModalAlert
        isOpen={!!alertState.alert}
        onClose={alertState.hideAlert}
        onConfirm={alertState.alert?.onConfirm}
        type={alertState.alert?.type}
        title={alertState.alert?.title}
        message={alertState.alert?.message}
        confirmText={alertState.alert?.confirmText}
        cancelText={alertState.alert?.cancelText}
        showCancel={alertState.alert?.showCancel}
      />
    </ModalAlertContext.Provider>
  );
};

export default ModalAlert;
